import React from 'react';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { DATA_LAYERS } from '../../../lib/dataLayers';
import { useImperiledSpecies } from '../../../hooks/useImperiledSpecies';
import { IMPERILED_SPECIES_PALETTE } from '../../../lib/imperiledSpeciesColors';
import {
  CHART_TOOLTIP_STYLE,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_TOOLTIP_ITEM_STYLE,
  CHART_AXIS_STYLE,
  CHART_GRID_STROKE,
  CHART_AXIS_STROKE,
} from '../../../lib/chartStyles';
import { LayerHeader } from './LayerHeader';
import { AboutLayer } from './AboutLayer';
import { GlossaryTerm } from '../../shared/GlossaryTerm';
import {
  LayerLoadingLabel,
  LayerErrorAlert,
  LayerLiveBadge,
  SkeletonPulseBlock,
} from './contentPrimitives';

interface ImperiledSpeciesContentProps {
  millCode: string | null;
  driveTimeMinutes: number;
}

const Skeleton: React.FC = () => (
  <div className="space-y-4">
    <LayerLoadingLabel layer={DATA_LAYERS.imperiled_species} />
    <SkeletonPulseBlock className="h-[240px]" />
  </div>
);

const fmtAcres = (n: number) =>
  n >= 1000 ? `${Math.round(n).toLocaleString()} ac` : `${n.toFixed(1)} ac`;
const fmtPct = (f: number) => `${(f * 100).toFixed(1)}%`;

export const ImperiledSpeciesContent: React.FC<ImperiledSpeciesContentProps> = ({
  millCode,
  driveTimeMinutes,
}) => {
  const layer = DATA_LAYERS.imperiled_species;
  const { data, isLoading, error } = useImperiledSpecies(millCode, driveTimeMinutes);

  const chartData = (data?.classes ?? []).map(c => ({
    class: c.class,
    label: IMPERILED_SPECIES_PALETTE.labels[c.class],
    percent: c.fraction * 100,
    acres: c.signalAcres,
  }));

  // "Top-tier" = pixels in the national 95th-percentile-and-up classes.
  const topTierFraction = data
    ? data.classes
        .filter(c => c.class === 'high' || c.class === 'very_high')
        .reduce((sum, c) => sum + c.signalAcres, 0) / Math.max(data.totalAcres, 1)
    : 0;

  return (
    <div className="space-y-6">
      <LayerHeader layer={layer} />

      <AboutLayer>
        <p>
          The <GlossaryTerm term="mobi">Map of Biodiversity Importance (MoBI)</GlossaryTerm>{' '}
          highlights places where imperiled species &mdash; those ranked Critically
          Imperiled (<GlossaryTerm term="g1_g2">G1</GlossaryTerm>), Imperiled (G2), or
          listed under the Endangered Species Act &mdash; are most likely to occur.
          The <GlossaryTerm term="pwrsr">Protection-Weighted Range-Size Rarity (PWRSR)</GlossaryTerm>{' '}
          variant shown here combines each species&rsquo; habitat rarity with how much
          of its range is already protected, so hotspots are the small-range, unprotected
          places where conservation action does the most good.
        </p>
        <p>
          Values come from NatureServe&rsquo;s 2024 MoBI release, modeled at 330&thinsp;m
          from habitat suitability for 2,493 imperiled species: 326 vertebrates, 233
          freshwater invertebrates (mussels, crayfishes), 63 pollinators (bumblebees,
          solitary bees, butterflies, skippers), and 1,871 vascular plants. The five
          classes come from national-percentile cuts of the positive-signal
          distribution &mdash; Very High is the national top 1% &mdash; so your
          shed&rsquo;s mix is directly comparable to CONUS.
        </p>
        <p>
          Bars below show the share of <em>signal-positive</em> area in each national
          class. The fraction of the shed with no modeled imperiled-species signal
          is reported separately; for many sheds it&rsquo;s the majority of the
          shed area, and that&rsquo;s expected &mdash; imperiled-species habitat is
          concentrated nationwide.
        </p>
      </AboutLayer>

      {isLoading && <Skeleton />}

      {!isLoading && error && (
        <LayerErrorAlert layer={layer} details={error} />
      )}

      {!isLoading && data && data.totalAcres > 0 && (
        <>
          <LayerLiveBadge />

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-figma-card rounded-figma-panel p-4 border border-figma-lime/30 shadow-figma-card">
              <p className="text-xs font-roboto text-neutral-400 mb-1">
                Shed with imperiled signal
              </p>
              <p className="text-2xl font-roboto-mono text-figma-text">
                {fmtPct(data.signalFraction)}
              </p>
            </div>
            <div className="bg-figma-card rounded-figma-panel p-4 border border-figma-lime/30 shadow-figma-card">
              <p className="text-xs font-roboto text-neutral-400 mb-1">
                In national top 5% (High + Very&nbsp;High)
              </p>
              <p className="text-2xl font-roboto-mono text-figma-text">
                {fmtPct(topTierFraction)}
              </p>
            </div>
          </div>

          <div className="bg-figma-card rounded-figma-panel p-4 border border-figma-lime/30 shadow-figma-card">
            <h4 className="text-base font-roboto font-bold text-figma-text mb-4">
              Share of Signal-Positive Area by Class <span className="text-neutral-500 font-normal">(%)</span>
            </h4>
            <ResponsiveContainer width="100%" height={240} minWidth={0}>
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 5, right: 16, left: 8, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} horizontal={false} />
                <XAxis
                  type="number"
                  domain={[0, 100]}
                  stroke={CHART_AXIS_STROKE}
                  style={CHART_AXIS_STYLE}
                  tickFormatter={(v: number) => `${v}%`}
                />
                <YAxis
                  type="category"
                  dataKey="label"
                  stroke={CHART_AXIS_STROKE}
                  style={CHART_AXIS_STYLE}
                  width={80}
                />
                <Tooltip
                  cursor={false}
                  contentStyle={CHART_TOOLTIP_STYLE}
                  labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                  itemStyle={CHART_TOOLTIP_ITEM_STYLE}
                  formatter={(value: number | undefined, _name: string | undefined, props: { payload?: { acres?: number } }) => {
                    const pct = value ?? 0;
                    const acres = props.payload?.acres ?? 0;
                    return [`${pct.toFixed(1)}%  (${fmtAcres(acres)})`, 'Share of signal'];
                  }}
                />
                <Bar dataKey="percent" isAnimationActive={false}>
                  {chartData.map(d => (
                    <Cell key={d.class} fill={IMPERILED_SPECIES_PALETTE.colors[d.class]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="flex items-center justify-between pt-3 mt-2 border-t border-figma-lime/20 text-sm font-roboto">
              <span className="text-neutral-400">No-signal area</span>
              <span className="font-roboto-mono text-neutral-300">
                {fmtPct(data.backgroundFraction)}
                <span className="text-neutral-500 ml-2">of shed</span>
              </span>
            </div>
            <p className="text-xs font-roboto italic text-neutral-500 mt-2 leading-relaxed">
              Bars and map colors share the same 5-class national classification &mdash;
              a yellow pixel on the map is in the same national top-1% bucket that
              the Very High bar counts.
            </p>
          </div>

          <p className="text-xs font-roboto italic text-neutral-500 leading-relaxed">
            Source: NatureServe. 2024. Map of Biodiversity Importance. NatureServe,
            Arlington, Virginia. Distributed under CC&nbsp;BY-NC-SA&nbsp;4.0.
          </p>
        </>
      )}
    </div>
  );
};
